/* =============================================================================
   GLOBAL.JS — StylishRugs Theme
   Core utilities, event bus, and base custom elements
   ============================================================================= */

'use strict';

/* ---------------------------------------------------------------------------
   Utility Functions
   --------------------------------------------------------------------------- */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      'a[href], area[href], input:not([disabled]):not([type="hidden"]):not([aria-hidden]), select:not([disabled]):not([aria-hidden]), textarea:not([disabled]):not([aria-hidden]), button:not([disabled]):not([aria-hidden]), iframe, object, [tabindex]:not([tabindex="-1"]), [contenteditable]'
    )
  );
}

function trapFocus(container, elementToFocus = container) {
  const focusableEls = getFocusableElements(container);
  const firstFocusable = focusableEls[0];
  const lastFocusable = focusableEls[focusableEls.length - 1];

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  container.setAttribute('tabindex', '-1');
  container._trapFocusHandler = handleKeydown;
  container.addEventListener('keydown', handleKeydown);
  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.querySelectorAll('[tabindex="-1"]').forEach((el) => {
    if (el._trapFocusHandler) {
      el.removeEventListener('keydown', el._trapFocusHandler);
      el.removeAttribute('tabindex');
      delete el._trapFocusHandler;
    }
  });
  if (elementToFocus) elementToFocus.focus();
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, limit) {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

function formatMoney(cents, currency = 'USD') {
  const amount = (cents / 100).toFixed(2);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/* ---------------------------------------------------------------------------
   Pub/Sub Event Bus — decoupled communication between components
   --------------------------------------------------------------------------- */
const PubSub = (() => {
  const subscribers = {};

  function subscribe(event, callback) {
    if (!subscribers[event]) subscribers[event] = [];
    subscribers[event].push(callback);
    return () => {
      subscribers[event] = subscribers[event].filter((cb) => cb !== callback);
    };
  }

  function publish(event, data) {
    if (!subscribers[event]) return;
    subscribers[event].forEach((cb) => cb(data));
  }

  return { subscribe, publish };
})();

// Named event constants
const EVENTS = {
  CART_UPDATE: 'cart:update',
  CART_OPEN: 'cart:open',
  CART_CLOSE: 'cart:close',
  PRODUCT_ADDED: 'product:added',
  OVERLAY_SHOW: 'overlay:show',
  OVERLAY_HIDE: 'overlay:hide',
};

/* ---------------------------------------------------------------------------
   Cart API Helpers
   --------------------------------------------------------------------------- */
const CartAPI = {
  async getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async addItem(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(formData),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add item to cart');
    }
    return res.json();
  },

  async updateItem(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return res.json();
  },

  async removeItem(line) {
    return CartAPI.updateItem(line, 0);
  },

  async updateNote(note) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    return res.json();
  },
};

/* ---------------------------------------------------------------------------
   Cart Icon Bubble — live count in header
   --------------------------------------------------------------------------- */
function updateCartIconBubble(cart) {
  const bubble = document.getElementById('cart-icon-bubble');
  if (!bubble) return;
  const count = cart.item_count;
  if (count === 0) {
    bubble.innerHTML = '<span class="visually-hidden">Cart</span>';
    return;
  }
  bubble.innerHTML = `
    <div class="cart-count-bubble">
      <span aria-hidden="true">${count > 99 ? '99+' : count}</span>
      <span class="visually-hidden">${count} items in cart</span>
    </div>
  `;
}

PubSub.subscribe(EVENTS.CART_UPDATE, (cart) => updateCartIconBubble(cart));

/* ---------------------------------------------------------------------------
   Announcement Bar — auto-dismiss
   --------------------------------------------------------------------------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    const dismiss = this.querySelector('[data-dismiss]');
    if (!dismiss) return;
    dismiss.addEventListener('click', () => {
      this.style.height = this.offsetHeight + 'px';
      requestAnimationFrame(() => {
        this.style.transition = 'height 0.3s ease, opacity 0.3s ease';
        this.style.height = '0';
        this.style.opacity = '0';
        this.style.overflow = 'hidden';
      });
      sessionStorage.setItem('announcement-dismissed', 'true');
    });

    if (sessionStorage.getItem('announcement-dismissed') === 'true') {
      this.hidden = true;
    }
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------------------------------------------------------------------------
   Predictive Search
   --------------------------------------------------------------------------- */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.abortController = null;
    this._handleInput = debounce(this.fetchResults.bind(this), 300);
  }

  connectedCallback() {
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', this._handleInput);
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= 2) this.fetchResults();
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.closeResults();
    });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeResults();
        this.input.blur();
      }
    });
  }

  async fetchResults() {
    const q = this.input.value.trim();
    if (q.length < 2) { this.closeResults(); return; }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setAttribute('loading', '');
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`;
      const res = await fetch(url, { signal: this.abortController.signal });
      const data = await res.json();
      this.renderResults(data.resources.results, q);
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
    } finally {
      this.removeAttribute('loading');
    }
  }

  renderResults(results, query) {
    const products = results.products || [];
    const collections = results.collections || [];
    if (products.length === 0 && collections.length === 0) {
      this.closeResults();
      return;
    }

    let html = '';

    if (collections.length) {
      html += `<div class="predictive__group">
        <p class="predictive__group-label">Collections</p>
        <ul>
          ${collections.map(c => `
            <li class="predictive__item">
              <a href="${c.url}" class="predictive__item-link">
                <span class="predictive__item-title">${c.title}</span>
              </a>
            </li>
          `).join('')}
        </ul>
      </div>`;
    }

    if (products.length) {
      html += `<div class="predictive__group">
        <p class="predictive__group-label">Products</p>
        <ul>
          ${products.map(p => `
            <li class="predictive__item">
              <a href="${p.url}" class="predictive__item-link">
                ${p.featured_image ? `<img src="${p.featured_image.url}?width=80" alt="${p.title}" width="40" height="40" loading="lazy">` : ''}
                <div>
                  <span class="predictive__item-title">${p.title}</span>
                  ${p.price ? `<span class="predictive__item-price">${formatMoney(p.price)}</span>` : ''}
                </div>
              </a>
            </li>
          `).join('')}
        </ul>
      </div>`;
    }

    html += `<div class="predictive__footer">
      <a href="/search?q=${encodeURIComponent(query)}" class="predictive__view-all">
        View all results for "${query}"
      </a>
    </div>`;

    this.results.innerHTML = html;
    this.results.hidden = false;
    this.setAttribute('open', '');
  }

  closeResults() {
    if (this.results) this.results.hidden = true;
    this.removeAttribute('open');
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------------------------------------------------------------------------
   Mobile Menu Toggle
   --------------------------------------------------------------------------- */
class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.openTriggers = document.querySelectorAll('[data-open-menu]');
    this.closeTrigger = this.querySelector('[data-close-menu]');
    this.overlay = document.getElementById('overlay');

    this.openTriggers.forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });

    if (this.closeTrigger) {
      this.closeTrigger.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
    });
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    PubSub.publish(EVENTS.OVERLAY_SHOW);
    trapFocus(this);
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
    PubSub.publish(EVENTS.OVERLAY_HIDE);
    removeTrapFocus(document.querySelector('[data-open-menu]'));
  }
}
customElements.define('menu-drawer', MenuDrawer);

/* ---------------------------------------------------------------------------
   Overlay element
   --------------------------------------------------------------------------- */
PubSub.subscribe(EVENTS.OVERLAY_SHOW, () => {
  const overlay = document.getElementById('overlay');
  if (overlay) overlay.classList.add('is-visible');
  document.body.style.overflow = 'hidden';
});

PubSub.subscribe(EVENTS.OVERLAY_HIDE, () => {
  const overlay = document.getElementById('overlay');
  if (overlay) overlay.classList.remove('is-visible');
  document.body.style.overflow = '';
});

/* ---------------------------------------------------------------------------
   Share URL button (product page)
   --------------------------------------------------------------------------- */
class ShareUrl extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => {
      if (navigator.share) {
        navigator.share({
          title: document.title,
          url: location.href,
        });
      } else {
        navigator.clipboard.writeText(location.href).then(() => {
          const label = this.querySelector('[data-label]');
          if (label) {
            const original = label.textContent;
            label.textContent = 'Link copied!';
            setTimeout(() => { label.textContent = original; }, 2000);
          }
        });
      }
    });
  }
}
customElements.define('share-url', ShareUrl);

/* ---------------------------------------------------------------------------
   Image zoom on product page
   --------------------------------------------------------------------------- */
class ProductMediaGallery extends HTMLElement {
  connectedCallback() {
    this.thumbnails = this.querySelectorAll('[data-thumbnail]');
    this.mainImage = this.querySelector('[data-main-image]');

    this.thumbnails.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.switchImage(i));
    });
  }

  switchImage(index) {
    const src = this.thumbnails[index].dataset.src;
    const srcset = this.thumbnails[index].dataset.srcset;
    if (!this.mainImage || !src) return;

    this.thumbnails.forEach((t) => t.classList.remove('is-active'));
    this.thumbnails[index].classList.add('is-active');

    const img = this.mainImage.querySelector('img');
    if (img) {
      img.src = src;
      if (srcset) img.srcset = srcset;
    }
  }
}
customElements.define('product-media-gallery', ProductMediaGallery);

/* ---------------------------------------------------------------------------
   Sticky ATC — appears on mobile when main ATC scrolls out of view
   --------------------------------------------------------------------------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.mainAtc = document.querySelector('[data-main-atc]');
    if (!this.mainAtc) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    observer.observe(this.mainAtc);
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ---------------------------------------------------------------------------
   Collapsible / Accordion
   --------------------------------------------------------------------------- */
class CollapsibleContent extends HTMLElement {
  connectedCallback() {
    const trigger = this.querySelector('[data-trigger]');
    const content = this.querySelector('[data-collapsible-content]');
    if (!trigger || !content) return;

    trigger.addEventListener('click', () => {
      const isOpen = this.hasAttribute('open');
      if (isOpen) {
        this.removeAttribute('open');
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        this.setAttribute('open', '');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
  }
}
customElements.define('collapsible-content', CollapsibleContent);

/* ---------------------------------------------------------------------------
   Quantity Input Element
   --------------------------------------------------------------------------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.minus = this.querySelector('[data-minus]');
    this.plus = this.querySelector('[data-plus]');
    if (!this.input) return;

    this.minus?.addEventListener('click', () => this.change(-1));
    this.plus?.addEventListener('click', () => this.change(1));
  }

  change(delta) {
    const current = parseInt(this.input.value, 10) || 1;
    const min = parseInt(this.input.min, 10) || 1;
    const max = parseInt(this.input.max, 10) || Infinity;
    this.input.value = Math.min(Math.max(current + delta, min), max);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------------------------------------------------------------------------
   Init on DOM ready
   --------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  document.documentElement.classList.remove('no-js');

  // Create global overlay element if not already in HTML
  if (!document.getElementById('overlay')) {
    const overlay = document.createElement('div');
    overlay.id = 'overlay';
    overlay.className = 'overlay';
    document.body.appendChild(overlay);
    overlay.addEventListener('click', () => {
      PubSub.publish(EVENTS.OVERLAY_HIDE);
      PubSub.publish(EVENTS.CART_CLOSE);
    });
  }
});
